import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { isNumber } from 'shared/utils/is-number';
import { numberToPrintableValue } from 'shared/utils/number-to-printable-value';

import * as Styled from './resizable-input.style';

export type ResizableInputProps = React.ComponentProps<'input'> & {
  maxWidth?: string;
  minWidth?: string;
  icon?: React.FC;
  canClearEntry?: boolean;
  onClear?: () => void;
  showAsFormInput?: boolean;
  showAsSmallFormInput?: boolean;
  fullWidth?: boolean;
  inheritFont?: boolean;
  hideNumberButtons?: boolean;
  suffix?: string;
};

export const ResizableInput = forwardRef<HTMLInputElement, ResizableInputProps>(
  (
    {
      value,
      placeholder,
      minWidth = '6.2rem',
      maxWidth = '100%',
      icon,
      canClearEntry,
      onClear,
      showAsFormInput,
      showAsSmallFormInput,
      fullWidth,
      inheritFont,
      hideNumberButtons,
      suffix,
      type,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const inputRef = useForwardedRef(ref);
    const showClearButton = canClearEntry && !!value?.toString().length;

    const clearInput = () => {
      const valueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )!.set;
      if (!valueSetter || !inputRef.current) {
        return;
      }

      valueSetter.call(inputRef.current, '');
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      onClear?.();
    };

    const _onClick = (e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation();
      inputRef.current?.focus();
    };

    const increaseNumber = () => {
      inputRef.current?.focus();
      inputRef.current?.stepUp();
      inputRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const decreaseNumber = () => {
      inputRef.current?.focus();
      inputRef.current?.stepDown();
      inputRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
    };

    return (
      <Styled.Container
        $minWidth={minWidth}
        $maxWidth={maxWidth}
        $fullWidth={!!fullWidth}
      >
        {!!icon && (
          <Styled.IconContainer $hasValue={!!value?.toString().length}>
            <Icon icon={icon} />
          </Styled.IconContainer>
        )}
        <Styled.SizingSpan
          $hasIconLeft={!!icon}
          $hasIconRight={!!showClearButton}
          $hasNumberButtons={type === 'number' && !hideNumberButtons}
          $showAsFormInput={!!showAsFormInput}
          $showAsSmallFormInput={!!showAsSmallFormInput}
          $inheritFont={!!inheritFont}
        >
          {isNumber(value)
            ? numberToPrintableValue(value as number)
            : value || placeholder}
          {suffix ? ` ${suffix}` : ''}
        </Styled.SizingSpan>
        <Styled.Input
          ref={inputRef}
          value={value}
          placeholder={placeholder}
          $hasIconLeft={!!icon}
          $hasIconRight={!!showClearButton}
          $showAsFormInput={!!showAsFormInput}
          $showAsSmallFormInput={!!showAsSmallFormInput}
          $inheritFont={!!inheritFont}
          $hasNumberButtons={type === 'number' && !hideNumberButtons}
          onClick={_onClick}
          type={type}
          {...rest}
        />

        {!!suffix && (
          <Styled.Suffix
            $showAsFormInput={!!showAsFormInput}
            $showAsSmallFormInput={!!showAsSmallFormInput}
            $hasIconRight={!!showClearButton}
            $hasNumberButtons={type === 'number' && !hideNumberButtons}
          >
            {suffix}
          </Styled.Suffix>
        )}

        {type === 'number' && !hideNumberButtons && (
          <Styled.NumberButtonsContainer
            $clearButtonSpacing={!!showClearButton}
          >
            <Styled.NumberButton
              onClick={increaseNumber}
              type="button"
              tabIndex={-1}
            >
              <Icon icon={ChevronLeft} />
            </Styled.NumberButton>

            <Styled.NumberButton
              onClick={decreaseNumber}
              type="button"
              tabIndex={-1}
              $isSecond
            >
              <Icon icon={ChevronLeft} />
            </Styled.NumberButton>
          </Styled.NumberButtonsContainer>
        )}

        {showClearButton && (
          <Styled.ClearButton
            onClick={clearInput}
            aria-label={t('general.labels.clear-field')}
            type="button"
          >
            <Icon icon={X} />
          </Styled.ClearButton>
        )}
      </Styled.Container>
    );
  },
);
